import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/context/AuthContext";

/**
 * Safe resend-verification helper (generic API response; no account enumeration).
 */
export default function ResendVerificationPanel({ email, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [devLink, setDevLink] = useState("");

  const submit = async () => {
    setError("");
    setMessage("");
    setDevLink("");
    const trimmed = (email || "").trim();
    if (!trimmed) {
      setError("Enter your email address above, then resend verification.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email: trimmed });
      setMessage(
        data?.message ||
          "If an unverified account exists for that email, a verification link has been issued."
      );
      if (data?.dev_verification_link) {
        setDevLink(data.dev_verification_link);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded border border-border bg-muted/30 p-4 space-y-3"
      data-testid="resend-verification-panel"
      role="region"
      aria-label="Resend email verification"
    >
      <p className="text-sm">
        Password reset does not verify your email. You still need to open the verification link we sent when you
        registered (or request a new link below).
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" className="btn-outline min-h-11" disabled={busy} onClick={submit} data-testid="resend-verification-submit">
          {busy ? "Sending…" : "Resend verification email"}
        </button>
        {onDismiss && (
          <button type="button" className="text-sm underline" onClick={onDismiss} data-testid="resend-verification-dismiss">
            Dismiss
          </button>
        )}
      </div>
      {message && (
        <p className="text-sm" data-testid="resend-verification-message">
          {message}
        </p>
      )}
      {devLink && (
        <p className="text-sm text-muted-foreground">
          Development mode:{" "}
          <Link to={devLink.startsWith("/") ? devLink : `/${devLink}`} className="underline" data-testid="dev-resend-verification-link">
            Open verification link
          </Link>
        </p>
      )}
      {error && (
        <p className="text-sm text-destructive" role="alert" data-testid="resend-verification-error">
          {error}
        </p>
      )}
    </div>
  );
}
